import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { 
  Users,
  Eye,
  Phone,
  Mail,
  Calendar,
  ArrowLeft,
  FileText,
  TrendingUp,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import DashboardLayout from "../../components/DashboardLayout";
import ClientSearchFilters from "../../components/admin/ClientSearchFilters";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminClientSearch = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    total_pages: 0
  });
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (params) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await axios.post(`${API}/admin/clients/search`, params);
      setClients(res.data.clients);
      setPagination({
        total: res.data.total,
        page: res.data.page,
        limit: res.data.limit,
        total_pages: res.data.total_pages
      });
    } catch (error) {
      toast.error("Error al buscar clientes");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    handleSearch({ ...pagination, page: newPage });
  };

  const getStageLabel = (step) => {
    const stages = {
      1: "Activación",
      2: "Análisis",
      3: "Documentación",
      4: "Revisión",
      5: "Pago",
      6: "Cita",
      7: "Preparación",
      8: "Entrevista",
      9: "Resultado"
    };
    return stages[step] || `Etapa ${step}`;
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return "bg-emerald-500";
    if (progress >= 50) return "bg-amber-500";
    return "bg-navy-300";
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="p-2 hover:bg-navy-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-navy-500" />
          </Link>
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900">Búsqueda de Clientes</h1>
            <p className="text-navy-500">Encuentra clientes con filtros avanzados</p>
          </div>
        </div>

        {/* Search Filters Component */}
        <ClientSearchFilters onSearch={handleSearch} loading={loading} />

        {/* Results */}
        {!hasSearched ? (
          <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
            <Users className="w-16 h-16 text-navy-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-navy-900 mb-2">Busca Clientes</h3>
            <p className="text-navy-500 text-sm max-w-md mx-auto">
              Usa la barra de búsqueda o los filtros para encontrar clientes específicos
            </p>
          </div>
        ) : loading ? (
          <div className="bg-white rounded-xl border border-navy-100 p-12 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-navy-900 mb-2">Sin Resultados</h3>
            <p className="text-navy-500 text-sm">No se encontraron clientes con los filtros aplicados</p>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-navy-500">
                Mostrando <span className="font-semibold text-navy-900">{clients.length}</span> de{" "}
                <span className="font-semibold text-navy-900">{pagination.total}</span> clientes
              </p>
            </div>

            {/* Clients Grid */}
            <div className="grid gap-4">
              {clients.map((client) => (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-navy-100 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                        {client.name?.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-navy-900">{client.name}</h3>
                          {client.pending_docs_count > 0 && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {client.pending_docs_count} pendiente(s)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-navy-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {client.email}
                          </span>
                          {client.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {client.phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> 
                            {new Date(client.created_at).toLocaleDateString('es-MX')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right Side */}
                    <div className="flex items-center gap-6">
                      {/* Progress */}
                      <div className="text-right">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm text-navy-500">Etapa {client.current_step}:</span>
                          <span className="text-sm font-medium text-navy-900">
                            {getStageLabel(client.current_step)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-navy-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getProgressColor(client.progress)} transition-all`}
                              style={{ width: `${client.progress}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-navy-600">{client.progress}%</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <Link to={`/admin/cases/${client.case?.id}`}>
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-2" />
                          Ver Caso
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          pageNum === pagination.page
                            ? 'bg-emerald-600 text-white'
                            : 'hover:bg-navy-100 text-navy-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminClientSearch;
